// Qué deja filtrar cada columna de la tabla de cargas de salida.
//
// Es la ÚNICA tabla de correspondencias entre una columna y su filtro, y vive
// fuera de la página para que se pueda leer de un vistazo y probar sin montar
// nada. La clave de cada entrada es la `key` de la columna
// (`columnasCargaSalida`), más `carga`, que es una cabecera escrita a mano en
// la tabla porque lleva el botón de desplegar.
//
// LO QUE NO ESTÁ AQUÍ ES DELIBERADO. Artículo y empresa no se pintan en ninguna
// columna, así que se quedan en la barra de arriba: colgarlos de una cabecera
// cualquiera sería mentir sobre lo que filtran. Los tres «sólo» —aéreas,
// atrasadas, con incidencia— tampoco son de una columna: cruzan varias.
//
// Y NINGÚN FILTRO ESTÁ EN LOS DOS SITIOS. El estado es uno (`filtros`), pero
// dos controles sobre el mismo campo son dos sitios donde se descuadra el
// catálogo en cascada sin que nada dé error.
import type { TFunction } from 'i18next'

import { tieneAlgoQueOfrecer, type FiltroColumna } from '@/utils/filtroColumna'
import type { OpcionMulti } from '@/components/common/MultiFiltro'
import type { CargaEstadoPicking, EstadoCarga } from '@/types'
import type {
  CargasSalidaFiltrosState, FiltroMuelle, FiltroPaletizada, FiltroReserva,
} from './CargasSalidaFiltros'

/** Los catálogos en cascada que la página ya calcula. */
export interface CatalogosCabecera {
  carga: OpcionMulti[]
  pedido: OpcionMulti[]
  cliente: OpcionMulti[]
  almacenes: string[]
  grupo: OpcionMulti[]
  incoterm: OpcionMulti[]
  modo: OpcionMulti[]
  estado: OpcionMulti[]
}

export function filtrosDeColumna(
  f: CargasSalidaFiltrosState,
  onChange: (patch: Partial<CargasSalidaFiltrosState>) => void,
  cat: CatalogosCabecera,
  t: TFunction,
): Record<string, FiltroColumna> {
  const crudo: Record<string, FiltroColumna> = {
    carga: [{
      tipo: 'lista',
      opciones: cat.carga,
      seleccion: f.carga,
      onSeleccion: carga => onChange({ carga }),
      // Texto libre para poder PEGAR una columna de nº de carga de Excel, que
      // es lo que hacía el buscador de la barra antes de mudarse aquí.
      permitirLibre: true,
    }],

    // Dos pestañas: la columna pinta el cliente y el pedido, así que su
    // cabecera ofrece los dos. Son filtros distintos que se cruzan con Y —«este
    // cliente Y este pedido»—, no uno con dos nombres.
    clientePedido: [
      {
        tipo: 'lista',
        nombre: t('cargasSalida.filtros.cliente'),
        opciones: cat.cliente,
        seleccion: f.cliente,
        onSeleccion: cliente => onChange({ cliente }),
        // Se elige por NOMBRE, que es lo que la tabla pinta; el código va de
        // detalle y el buscador del panel también mira en él, así que pegarlo
        // sigue funcionando.
        titulosMono: false,
      },
      {
        tipo: 'lista',
        nombre: t('cargasSalida.filtros.pedido'),
        opciones: cat.pedido,
        seleccion: f.pedido,
        onSeleccion: pedido => onChange({ pedido }),
        permitirLibre: true,
      },
    ],

    almacen: [{
      tipo: 'lista',
      opciones: cat.almacenes.map(v => ({ valor: v, titulo: v })),
      seleccion: f.almacen,
      onSeleccion: almacen => onChange({ almacen }),
    }],

    // Grupo, incoterm y modo llevan el hueco como una opción más (lo pone
    // `opcionesConHueco`): sin ella, las cargas sin dato desaparecerían al
    // filtrar sin que nada lo dijera.
    grupoCliente: [{
      tipo: 'lista',
      opciones: cat.grupo,
      seleccion: f.grupo,
      onSeleccion: grupo => onChange({ grupo }),
      titulosMono: false,
    }],

    incoterm: [{
      tipo: 'lista',
      opciones: cat.incoterm,
      seleccion: f.incoterm,
      onSeleccion: incoterm => onChange({ incoterm }),
    }],

    fechaSalida: [{
      tipo: 'rango',
      desde: f.desde,
      hasta: f.hasta,
      onDesde: desde => onChange({ desde }),
      onHasta: hasta => onChange({ hasta }),
    }],

    // Por el modo YA RESUELTO, que es lo que la columna enseña: por el campo
    // crudo, pedir AEREO dejaría fuera 195 de las 227 cargas que se pintan
    // como aéreas.
    transporte: [{
      tipo: 'lista',
      opciones: cat.modo,
      seleccion: f.modo,
      onSeleccion: modo => onChange({ modo }),
      titulosMono: false,
    }],

    // Valor único: los tres estados son un reparto completo de las cargas, así
    // que elegir dos sólo sirve para pedir «no completa», y para eso están «sin
    // reserva» y «parcial» por separado.
    reserva: [{
      tipo: 'lista',
      unico: true,
      opciones: (['sin', 'parcial', 'completa'] as const).map(v => ({
        valor: v,
        titulo: t(`cargasSalida.filtros.reserva${
          v === 'completa' ? 'Completa' : v === 'parcial' ? 'Parcial' : 'Sin'
        }`),
      })),
      seleccion: f.reserva ? [f.reserva] : [],
      onSeleccion: v => onChange({ reserva: (v[v.length - 1] ?? '') as FiltroReserva }),
      titulosMono: false,
    }],

    picking: [{
      tipo: 'lista',
      opciones: (['pendiente', 'preparando', 'preparada'] as const).map(v => ({
        valor: v, titulo: t(`salesOrders.picking.estado.${v}`),
      })),
      seleccion: f.estadoPicking,
      onSeleccion: v => onChange({ estadoPicking: v as CargaEstadoPicking[] }),
      titulosMono: false,
    }],

    // La columna «Estado» pinta DOS chapas, así que su cabecera ofrece las dos.
    // Con una sola se filtraba por paletizada bajo un rótulo que dice «Estado»:
    // ni se podía pedir «las que están listas» ni se entendía lo que ofrecía.
    estado: [{
      // El estado de cabecera. Sale en cascada —`cargado` sólo aparece
      // si hay cargas expedidas cargadas— porque ofrecer una opción que no
      // selecciona nada es otra forma de mentir.
      tipo: 'lista',
      nombre: t('cargasSalida.tabla.columnas.estado'),
      opciones: cat.estado,
      seleccion: f.estado,
      onSeleccion: v => onChange({ estado: v as EstadoCarga[] }),
      titulosMono: false,
    }, {
      // «No consta» es una opción de verdad: el campo viene vacío en 700 de las
      // 1.223 cargas vivas, y sin ella no habría forma de preguntar por ellas.
      tipo: 'lista',
      nombre: t('cargasSalida.filtros.paletizada'),
      opciones: [
        { valor: 'si', titulo: t('salesOrders.picking.paletizada') },
        { valor: 'no', titulo: t('salesOrders.picking.noPaletizada') },
        { valor: 'noConsta', titulo: t('cargasSalida.filtros.noConsta') },
      ],
      seleccion: f.paletizada,
      onSeleccion: v => onChange({ paletizada: v as FiltroPaletizada[] }),
      titulosMono: false,
    }],

    muelle: [{
      tipo: 'lista',
      opciones: [
        { valor: 'con', titulo: t('cargasSalida.filtros.conMuelle') },
        { valor: 'sin', titulo: t('cargasSalida.filtros.sinMuelle') },
      ],
      seleccion: f.muelle,
      onSeleccion: v => onChange({ muelle: v as FiltroMuelle[] }),
      titulosMono: false,
    }],
  }

  // Fuera las secciones sin nada que ofrecer, y fuera la columna que se queda
  // sin ninguna: un embudo que abre un panel vacío parece un filtro roto.
  const util: Record<string, FiltroColumna> = {}
  for (const [clave, secciones] of Object.entries(crudo)) {
    const vivas = secciones.filter(tieneAlgoQueOfrecer)
    if (vivas.length > 0) util[clave] = vivas
  }
  return util
}
